<template>
  <SubscriptionNavigation
    @submit-clicked="onSubmit"
    :is-submitting-data="isLoadingUpdatePhysicalBattery"
    :is-loading-data="isGetSubscriptionInProgress || isLoadingPhysicalBatteries"
  >
    <div data-cy="physical-battery-page">
      <div class="text-center">
        <AtomText
          :text="t('subscription.physicalBattery.title')"
          font-style="font-semibold"
          font-size="text-xl"
          data-cy="physical-battery-page-title"
        />
        <AtomText
          :text="t('subscription.physicalBattery.subtitle')"
          font-size="text-base"
          color-variant="description"
          class="mt-2"
          data-cy="physical-battery-page-subtitle"
        />
      </div>

      <div data-cy="physical-battery-brand" class="mt-6">
        <MoleculeLabel
          :text="t('subscription.physicalBattery.form.brand.label')"
          class="mb-1"
          label-for="physicalBatteryName"
        />
        <OrganismSelect
          v-model="selectedBrandOption"
          :options="brandOptions"
          name="physicalBatteryName"
          :placeholder="t('subscription.physicalBattery.form.brand.placeholder')"
          select-trigger-aria-label="physical-battery-brand-select"
          data-cy="physical-battery-brand-select"
          :disabled="isFormDisabled"
          :select-state="getInputState({ errorMessage: isSubmitted ? brandError : undefined })"
          :helper="getHelperMessage({ errorMessage: isSubmitted ? brandError : undefined })"
        />
      </div>

      <div data-cy="physical-battery-capacity" class="mt-6">
        <MoleculeLabel
          :text="t('subscription.physicalBattery.form.capacity.label')"
          class="mb-1"
          label-for="capacityInkWh"
        />
        <MoleculeInputNumber
          id="capacityInkWh"
          v-model="capacityInkWh"
          step="0.1"
          :addon-text="{
            value: 'kWh',
            isPrefix: false,
          }"
          :placeholder="t('subscription.physicalBattery.form.capacity.placeholder')"
          addon-type="text"
          name="capacityInkWh"
          data-cy="physical-battery-capacity-input"
          :disabled="isFormDisabled"
          :input-state="getInputState({ errorMessage: isSubmitted ? capacityError : undefined })"
          :helper="
            getHelperMessage({
              errorMessage: isSubmitted ? capacityError : undefined,
              infoMessage: t('subscription.physicalBattery.form.capacity.helper.info'),
            })
          "
          @error="onCapacityInputError"
          @input="onCapacityInput"
        />
      </div>

      <MoleculeFrameCard
        v-if="isFormFilled"
        :main-theme="isEligibleForEncore ? 'success' : 'dark'"
        spacing="sm"
        class="mt-6"
        :data-cy="encoreCardContent.dataCy"
      >
        <div class="flex items-center gap-1">
          <AtomPicto name="myBattery" alt="physicalBattery" size="md" class="mr-3" />
          <div class="flex min-w-0 flex-1 flex-col items-start gap-1">
            <MoleculeTag
              :value="t('subscription.physicalBattery.encore.tag')"
              type="offers"
              size="lg"
            />
            <AtomText :text="encoreCardContent.title" font-style="font-semibold" />
            <AtomText :text="encoreCardContent.description" font-size="text-sm" />
          </div>
        </div>
        <MoleculeStandaloneLink
          :label="t('subscription.physicalBattery.encore.learnMore')"
          to=""
          font-size="sm"
          font-weight="medium"
          color-variant="brand"
          class="mt-3"
          data-cy="physical-battery-encore-learn-more-link"
          @click="isHowEncoreWorksModalOpen = true"
        />
      </MoleculeFrameCard>
    </div>

    <HowEncoreWorksModal v-model:is-open="isHowEncoreWorksModalOpen" />
  </SubscriptionNavigation>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useField } from 'vee-validate';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

import useInputState from '@/composables/useInputState.composable';
import {
  usePhysicalBatteryStore,
  useStepStore,
  useSubscriptionStore,
  useToastSnackbarStore,
} from '@/stores';

import type { PhysicalBatteryName } from '@/types/subscription.type';
import { formatDecimal, formatEuroPrice } from '@/utils/format.util';

import HowEncoreWorksModal from '@/components/subscription/HowEncoreWorksModal.component.vue';
import SubscriptionNavigation from '@/components/SubscriptionNavigation.component.vue';

import { PHYSICAL_BATTERY_MIN_CAPACITY_KWH } from '@/constants/subscription.constants';
import * as Sentry from '@sentry/vue';
import { useDebounceFn } from '@vueuse/core';

type BrandSelectOption = {
  id: number;
  value: PhysicalBatteryName;
  label: string;
};

const { t } = useI18n();

const subscriptionStore = useSubscriptionStore();
const physicalBatteryStore = usePhysicalBatteryStore();
const { addErrorToast, clearToasts } = useToastSnackbarStore();
const { goToNextStep } = useStepStore();

const { getInputState, getHelperMessage } = useInputState();

const { isGetSubscriptionInProgress, getCurrentSubscriptionInstallation } =
  storeToRefs(subscriptionStore);

const {
  physicalBatteries,
  minimumPhysicalBatteryCapacityInkWh,
  monthlyDiscountPerkWh,
  isLoadingPhysicalBatteries,
  isLoadingUpdatePhysicalBattery,
} = storeToRefs(physicalBatteryStore);

const { findPhysicalBatteries, updatePhysicalBattery } = physicalBatteryStore;

const {
  value: physicalBatteryName,
  errorMessage: brandError,
  validate: validateBrand,
} = useField<PhysicalBatteryName | ''>(
  'physicalBatteryName',
  yup.string().required(t('common.forms.required'))
);

const capacityRequiredSchema = yup
  .number()
  .transform((value, originalValue) => (originalValue === '' ? undefined : value))
  .typeError(t('common.forms.invalidFormat'))
  .required(t('common.forms.required'))
  .test(
    'one-decimal-max',
    t('subscription.physicalBattery.form.capacity.helper.decimals'),
    value => value === undefined || Math.abs(value * 10 - Math.round(value * 10)) < 1e-9
  );

const {
  value: capacityInkWh,
  errorMessage: capacityError,
  validate: validateCapacity,
} = useField<number | string | undefined>(
  'capacityInkWh',
  capacityRequiredSchema.min(
    PHYSICAL_BATTERY_MIN_CAPACITY_KWH,
    t('subscription.physicalBattery.form.capacity.helper.min', {
      min: PHYSICAL_BATTERY_MIN_CAPACITY_KWH,
    })
  )
);

const isSubmitted = ref(false);
const isHowEncoreWorksModalOpen = ref(false);

const isFormDisabled = computed(
  () =>
    isGetSubscriptionInProgress.value ||
    isLoadingPhysicalBatteries.value ||
    isLoadingUpdatePhysicalBattery.value
);

// The number input only emits its model on blur and rejects some values through its `error`
// event instead: we forward them to the field so that yup produces the error message
const onCapacityInputError = (_errorTypes: string[], value: string) => {
  capacityInkWh.value = value;
};

const onCapacityInput = useDebounceFn((event: Event) => {
  if (event.target instanceof HTMLInputElement) {
    capacityInkWh.value = event.target.value.replace(',', '.');
  }
}, 300);

const brandOptions = computed<BrandSelectOption[]>(() =>
  physicalBatteries.value.map((battery, index) => ({
    id: index,
    value: battery.physicalBatteryName,
    label: t(`subscription.physicalBattery.form.brand.options.${battery.physicalBatteryName}`),
  }))
);

const selectedBrandOption = computed<BrandSelectOption | null>({
  get: () => brandOptions.value.find(option => option.value === physicalBatteryName.value) ?? null,
  set: option => {
    physicalBatteryName.value = option?.value ?? '';
  },
});

const hasCapacityFilled = computed(() => capacityRequiredSchema.isValidSync(capacityInkWh.value));

const isBrandCompatible = computed(
  () =>
    physicalBatteries.value.find(
      battery => battery.physicalBatteryName === physicalBatteryName.value
    )?.compatibleWithBatteryOption === true
);

const isCapacitySufficient = computed(
  () =>
    hasCapacityFilled.value &&
    minimumPhysicalBatteryCapacityInkWh.value !== null &&
    Number(capacityInkWh.value) >= minimumPhysicalBatteryCapacityInkWh.value
);

const isEligibleForEncore = computed(() => isBrandCompatible.value && isCapacitySufficient.value);

const isFormFilled = computed(() => !!physicalBatteryName.value && hasCapacityFilled.value);

// Informative maximum (MySmartBattery rate). The actual discount shown later in the funnel is
// computed by the back.
const maxMonthlyDiscount = computed(
  () =>
    Math.round(
      Number(capacityInkWh.value) * (monthlyDiscountPerkWh.value?.mySmartBattery ?? 0) * 100
    ) / 100
);

// An unsupported brand takes precedence over a too low capacity
const encoreCardContent = computed(() => {
  const brand = selectedBrandOption.value?.label ?? '';
  const capacity = formatDecimal(Number(capacityInkWh.value));

  if (!isBrandCompatible.value) {
    return {
      dataCy: 'physical-battery-encore-brand-not-compatible-card',
      title: t('subscription.physicalBattery.encore.brandNotCompatible.title'),
      description: t('subscription.physicalBattery.encore.brandNotCompatible.description', {
        brand,
      }),
    };
  }

  if (!isCapacitySufficient.value) {
    return {
      dataCy: 'physical-battery-encore-capacity-too-low-card',
      title: t('subscription.physicalBattery.encore.capacityTooLow.title'),
      description: t('subscription.physicalBattery.encore.capacityTooLow.description', {
        brand,
        capacity,
        minimumCapacity: minimumPhysicalBatteryCapacityInkWh.value,
      }),
    };
  }

  return {
    dataCy: 'physical-battery-encore-compatible-card',
    title: t('subscription.physicalBattery.encore.compatible.title', {
      maxDiscount: formatEuroPrice(maxMonthlyDiscount.value),
    }),
    description: t('subscription.physicalBattery.encore.compatible.description', {
      brand,
      capacity,
      mySmartBatteryRate: formatEuroPrice(monthlyDiscountPerkWh.value?.mySmartBattery ?? null),
      myBatteryRate: formatEuroPrice(monthlyDiscountPerkWh.value?.myBattery ?? null),
    }),
  };
});

const setDefaultValues = () => {
  const physicalBattery = getCurrentSubscriptionInstallation.value?.physicalBattery;

  physicalBatteryName.value = physicalBattery?.physicalBatteryName ?? '';
  capacityInkWh.value = physicalBattery?.capacityInkWh;
};

const onSubmit = async () => {
  isSubmitted.value = true;

  clearToasts();

  const [brandValidation, capacityValidation] = await Promise.all([
    validateBrand(),
    validateCapacity(),
  ]);

  if (!brandValidation.valid || !capacityValidation.valid) {
    return;
  }

  try {
    await updatePhysicalBattery({
      physicalBatteryName: physicalBatteryName.value as PhysicalBatteryName,
      capacityInkWh: Number(capacityInkWh.value),
    });

    goToNextStep();
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'physical-battery', action: 'updatePhysicalBattery' },
    });
    addErrorToast({
      id: 'physical-battery-update-error',
      title: t('common.apiError'),
      content: t('subscription.physicalBattery.api.errors.patchSubscriptionPhysicalBattery'),
    });
  }
};

onMounted(async () => {
  try {
    await findPhysicalBatteries();
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'physical-battery', action: 'findPhysicalBatteries' },
    });
    addErrorToast({
      id: 'physical-batteries-find-error',
      title: t('common.apiError'),
      content: t('subscription.physicalBattery.api.errors.getPhysicalBatteries'),
    });
  }

  setDefaultValues();
});
</script>
